import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useWaitlistAnalytics } from '../../hooks/useAnalytics';
import MetricCard from '../dashboard/core/MetricCard';
import AreaChart from '../ui/AreaChart';
import BarChart from '../ui/BarChart';
import PieChart from '../ui/PieChart';
import CollapsibleSection from '../ui/CollapsibleSection';
import { transformToChartData, transformToChartDataAsync } from '../../services/chartService';
import { getPreviousPeriodValue } from '../../utils/chartHelpers';
import { formatPercent } from '../../utils/format';
import { FILTER_PERIODS } from '../../constants';
import './WaitlistStats.css';

/**
 * Waitlist Stats Component
 * Displays waitlist subscriber statistics and analytics
 * @param {Object} props - Component props
 * @param {string} [props.period='day'] - Time period for metrics
 * @param {boolean} [props.compact] - Whether to display in compact mode
 */
const WaitlistStats = ({ period = FILTER_PERIODS.DAY, compact = false }) => {
  const { data: waitlistData, loading, error } = useWaitlistAnalytics(period);

  // Generate chart data for key metrics using real data (must be before any early returns)
  const [newSubscribersChart, setNewSubscribersChart] = useState(null);
  const [verifiedSubscribersChart, setVerifiedSubscribersChart] = useState(null);
  const [previousWaitlistData, setPreviousWaitlistData] = useState(null);

  // Fetch previous period data for accurate comparison
  useEffect(() => {
    const fetchPreviousPeriodData = async () => {
      try {
        const prevPeriodMap = {
          [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
          [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
          [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
          [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
          [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
        };
        
        const prevPeriod = prevPeriodMap[period] || FILTER_PERIODS.DAY;
        const { getWaitlistAnalytics } = await import('../../services/firebaseService');
        const prevWaitlist = await getWaitlistAnalytics(prevPeriod).catch(() => null);
        setPreviousWaitlistData(prevWaitlist);
      } catch (error) {
        setPreviousWaitlistData(null);
      }
    };

    if (waitlistData) {
      fetchPreviousPeriodData();
    }
  }, [period, waitlistData]);

  useEffect(() => {
    if (!waitlistData) {
      setNewSubscribersChart(null);
      setVerifiedSubscribersChart(null);
      return;
    }

    // Use actual previous period data if available, otherwise calculate from growth or use estimate
    const currentNew = waitlistData.newSubscribers || 0;
    const previousNew = getPreviousPeriodValue(waitlistData, previousWaitlistData, 'newSubscribers', 0.9);

    const currentVerified = waitlistData.verifiedSubscribers || 0;
    const previousVerified = getPreviousPeriodValue(waitlistData, previousWaitlistData, 'verifiedSubscribers', 0.9);

    // Try to fetch real data from Waitlist collection
    Promise.all([
      transformToChartDataAsync(
        { newSubscribers: currentNew },
        { newSubscribers: previousNew },
        'newSubscribers',
        period,
        {
          collectionName: 'Waitlist',
          valueExtractor: () => 1 // Count subscribers
        }
      ),
      transformToChartDataAsync(
        { verifiedSubscribers: currentVerified },
        { verifiedSubscribers: previousVerified },
        'verifiedSubscribers',
        period,
        {
          collectionName: 'Waitlist',
          valueExtractor: (docData) => docData.emailSentStatus === 'success' ? 1 : 0 // Count verified
        }
      )
    ]).then(([newChart, verifiedChart]) => {
      setNewSubscribersChart(newChart);
      setVerifiedSubscribersChart(verifiedChart);
    }).catch(() => {
      // Fallback to synchronous generated data
      setNewSubscribersChart(transformToChartData(
        { newSubscribers: currentNew },
        { newSubscribers: previousNew },
        'newSubscribers',
        period
      ));
      setVerifiedSubscribersChart(transformToChartData(
        { verifiedSubscribers: currentVerified },
        { verifiedSubscribers: previousVerified },
        'verifiedSubscribers',
        period
      ));
    });
  }, [waitlistData, previousWaitlistData, period]);

  if (loading) {
    return (
      <div className="waitlist-stats metric-group" aria-live="polite" aria-busy="true">
        <div className="metric-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="waitlist-stats error">
        <h3>Error loading waitlist data</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="waitlist-stats metric-group">
      <h2 className="section-title"><i className="bi bi-card-checklist icon-brand" style={{ marginRight: 8 }}></i> Waitlist Statistics</h2>
      
      {/* Key Metrics - Always Visible */}
      <div className="stats-grid">
        <MetricCard
          title="Total Subscribers"
          value={typeof waitlistData?.totalSubscribers === 'number' ? waitlistData.totalSubscribers : 0}
          formatType="number"
          loading={loading}
          icon={<i className="bi bi-card-checklist icon-brand"></i>}
          color="blue"
          subtitle="all time"
          trend={typeof waitlistData?.growth?.totalSubscribers === 'number' ? Number(waitlistData.growth.totalSubscribers) : null}
          trendLabel={`${formatPercent(waitlistData?.growth?.totalSubscribers || 0, 1)} vs previous ${period}`}
        />
        <MetricCard
          title="New Subscribers"
          value={typeof waitlistData?.newSubscribers === 'number' ? waitlistData.newSubscribers : 0}
          formatType="number"
          loading={loading}
          icon={<i className="bi bi-person-plus icon-brand"></i>}
          color="green"
          subtitle={`${period} period`}
          trend={typeof waitlistData?.growth?.newSubscribers === 'number' ? Number(waitlistData.growth.newSubscribers) : null}
          trendLabel={`${formatPercent(waitlistData?.growth?.newSubscribers || 0, 1)} vs previous ${period}`}
        />
        <MetricCard
          title="Verified Subscribers"
          value={typeof waitlistData?.verifiedSubscribers === 'number' ? waitlistData.verifiedSubscribers : 0}
          formatType="number"
          loading={loading}
          icon={<i className="bi bi-patch-check icon-brand"></i>}
          color="teal"
          subtitle={`${formatPercent(waitlistData?.conversionRate || 0, 1)} conversion`}
        />
      </div>

      {/* Additional Metrics - Collapsible */}
      <CollapsibleSection
        title="Email & Verification Details"
        initialVisible={3}
        defaultExpanded={false}
      >
        {[
          <MetricCard
            key="email-success"
            title="Email Sent Success"
            value={typeof waitlistData?.emailSentSuccess === 'number' ? waitlistData.emailSentSuccess : 0}
            formatType="number"
            loading={loading}
            icon={<i className="bi bi-envelope-check icon-brand"></i>}
            color="purple"
            subtitle={`${formatPercent(waitlistData?.emailSuccessRate || 0, 1)} success rate`}
          />,
          <MetricCard
            key="email-failed"
            title="Email Failed"
            value={typeof waitlistData?.emailSentFailed === 'number' ? waitlistData.emailSentFailed : 0}
            formatType="number"
            loading={loading}
            icon={<i className="bi bi-x-circle icon-brand"></i>}
            color="red"
            subtitle="failed to send"
          />,
          <MetricCard
            key="email-pending"
            title="Email Pending"
            value={typeof waitlistData?.emailSentPending === 'number' ? waitlistData.emailSentPending : 0}
            formatType="number"
            loading={loading}
            icon={<i className="bi bi-hourglass-split icon-brand"></i>}
            color="orange"
            subtitle="pending sending"
          />,
          <MetricCard
            key="email-errors"
            title="Email Errors"
            value={typeof waitlistData?.emailErrors === 'number' ? waitlistData.emailErrors : 0}
            formatType="number"
            loading={loading}
            icon={<i className="bi bi-exclamation-triangle icon-brand"></i>}
            color="yellow"
            subtitle="configuration issues"
          />,
          <MetricCard
            key="pending-verification"
            title="Pending Verification"
            value={typeof waitlistData?.pendingVerification === 'number' ? waitlistData.pendingVerification : 0}
            formatType="number"
            loading={loading}
            icon={<i className="bi bi-shield-lock icon-brand"></i>}
            color="indigo"
            subtitle="awaiting verification"
          />,
          <MetricCard
            key="verification-codes"
            title="Verification Codes"
            value={typeof waitlistData?.verificationCount === 'number' ? waitlistData.verificationCount : 0}
            formatType="number"
            loading={loading}
            icon={<i className="bi bi-key icon-brand"></i>}
            color="cyan"
            subtitle="codes generated"
          />
        ]}
      </CollapsibleSection>
      
      {/* Chart sections - Using diverse visualizations */}
      {newSubscribersChart && (
        <AreaChart
          data={newSubscribersChart.data}
          comparisonData={newSubscribersChart.comparisonData}
          label="New Subscribers Over Time"
          formatType="number"
          color="green"
        />
      )}

      {verifiedSubscribersChart && (
        <AreaChart
          data={verifiedSubscribersChart.data}
          comparisonData={verifiedSubscribersChart.comparisonData}
          label="Verified Subscribers Over Time"
          formatType="number"
          color="teal"
        />
      )}

      {/* Email status distribution - Compact */}
      {waitlistData && (
        <div className={compact ? "chart-container-compact" : "chart-container"}>
          <PieChart
            data={[
              { label: 'Success', value: waitlistData.emailSentSuccess || 0 },
              { label: 'Failed', value: waitlistData.emailSentFailed || 0 },
              { label: 'Pending', value: waitlistData.emailSentPending || 0 }
            ].filter(item => item.value > 0)}
            label="Email Status Distribution"
            formatType="number"
            showPercentages={true}
            innerRadius={0.5}
          />
        </div>
      )}

      {/* Verification status comparison - Compact */}
      {waitlistData && (
        <div className={compact ? "chart-container-compact" : "chart-container"}>
          <BarChart
            data={[
              { label: 'Verified', value: waitlistData.verifiedSubscribers || 0 },
              { label: 'Pending', value: waitlistData.pendingVerification || 0 }
            ].filter(item => item.value > 0)}
            label="Verification Status"
            formatType="number"
            orientation="vertical"
          />
        </div>
      )}
    </div>
  );
};

WaitlistStats.propTypes = {
  period: PropTypes.string,
  compact: PropTypes.bool
};

WaitlistStats.defaultProps = {
  compact: false
};

export default WaitlistStats;
